import {
  formatCalendarDate,
  formatInstant,
  resolveDisplayZone,
  zonedBucketRange,
  type AdminSignupEvent,
  type AdminSignupStats,
} from "@checklist/shared";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { AdminNav } from "../components/admin/AdminNav.js";
import { ErrorBanner } from "../components/AuthShell.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { useAuth } from "../context/AuthContext.js";
import { ApiError, fetchAdminSignupStats } from "../lib/api.js";
import {
  bucketSignupEvents,
  summarizeSignupEvents,
  type SignupBucketCounts,
} from "../lib/signupStats.js";

type Days = 7 | 30 | 90;
type Grain = "day" | "hour";

const ATTEMPT_SERIES = [
  { key: "allowed", label: "Allowed", color: "#2563eb" },
  { key: "blockedTurnstile", label: "Blocked: bot check", color: "#d97706" },
  { key: "blockedAddress", label: "Blocked: address", color: "#7c3aed" },
  { key: "blockedGate", label: "Blocked: gate/breaker", color: "#64748b" },
] as const;

const CONFIRM_SERIES = [
  { key: "allowed", label: "Allowed", color: "#2563eb" },
  { key: "confirmed", label: "Confirmed", color: "#059669" },
] as const;

const OUTCOME_LABEL: Record<string, string> = {
  allowed: "Allowed",
  allowed_allowlist: "Allowed (allow list)",
  allowed_approved: "Allowed (approved)",
  rejected_turnstile: "Blocked: bot check",
  rejected_address: "Blocked: address",
  rejected_suppressed: "Blocked: suppressed",
  rejected_duplicate: "Blocked: duplicate",
  rejected_mode: "Blocked: gate",
  rejected_breaker: "Blocked: breaker",
};

/** D-3 quiet days, matching SIGNUP_GUARD.quietDaysByTrip. */
function quietDays(tripsInWindow: number): number | null {
  const table = [14, 30] as const;
  return table[Math.max(1, tripsInWindow) - 1] ?? null;
}

function rateLabel(rate: number | null): string {
  if (rate === null) return "\u2014";
  return `${Math.round(rate * 100)}%`;
}

function reasonLabel(reason: string | undefined): string {
  if (reason === "unconfirmed") return "unconfirmed sign-ups";
  if (reason === "volume") return "sign-up volume";
  return reason ?? "unknown";
}

function outcomeLabel(event: AdminSignupEvent): string {
  if (event.kind === "confirmed") return "Confirmed";
  if (event.outcome && OUTCOME_LABEL[event.outcome]) return OUTCOME_LABEL[event.outcome];
  return event.outcome ?? "Attempt";
}

function shortDay(key: string): string {
  return formatCalendarDate(key.slice(0, 10), { locale: "en-US" }).replace(/, \d{4}$/, "");
}

function hourClock(hour: number): string {
  if (hour === 0) return "12:00 AM";
  if (hour < 12) return `${hour}:00 AM`;
  if (hour === 12) return "12:00 PM";
  return `${hour - 12}:00 PM`;
}

function tooltipTitle(key: string, unit: Grain, zone: string): string {
  if (unit === "day") return `${formatCalendarDate(key, { locale: "en-US" })} (${zone})`;
  const hour = Number(key.slice(11, 13));
  return `${formatCalendarDate(key.slice(0, 10), { locale: "en-US" })}, ${hourClock(hour)} (${zone})`;
}

interface ChartSeries {
  label: string;
  color: string;
  values: number[];
}

function stackRects(
  series: ChartSeries[],
  index: number,
  x: number,
  barWidth: number,
  max: number,
  plotHeight: number,
  padTop: number
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let y = padTop + plotHeight;
  for (const item of series) {
    const value = item.values[index] ?? 0;
    const h = (value / max) * plotHeight;
    y -= h;
    nodes.push(
      <rect key={item.label} x={x} y={y} width={Math.max(barWidth, 1)} height={Math.max(h, 0)} fill={item.color} />
    );
  }
  return nodes;
}

function SignupChart({
  keys,
  unit,
  zone,
  series,
  stacked,
  ariaLabel,
}: {
  keys: string[];
  unit: Grain;
  zone: string;
  series: ChartSeries[];
  stacked: boolean;
  ariaLabel: string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const width = 720;
  const height = 228;
  const padLeft = 36;
  const padRight = 8;
  const padTop = 12;
  const padBottom = 36;
  const plotWidth = width - padLeft - padRight;
  const plotHeight = height - padTop - padBottom;
  const slot = plotWidth / Math.max(1, keys.length);
  const totals = keys.map((_, index) =>
    stacked
      ? series.reduce((sum, item) => sum + (item.values[index] ?? 0), 0)
      : Math.max(0, ...series.map((item) => item.values[index] ?? 0))
  );
  const max = Math.max(0, ...totals);
  if (!keys.length || max === 0) {
    return <p className="py-12 text-center text-sm text-muted">No sign-up activity in this range.</p>;
  }

  const yAt = (value: number) => padTop + plotHeight - (value / max) * plotHeight;
  const ticks = [...new Set([0, Math.round(max / 2), max])];

  function showTick(index: number): boolean {
    if (unit === "hour") return keys[index]?.endsWith("T00") ?? false;
    if (keys.length <= 10) return true;
    const step = Math.ceil(keys.length / 6);
    return index % step === 0 || index === keys.length - 1;
  }

  function setActiveFromPointer(clientX: number) {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return;
    // The viewBox is letterboxed inside the element (fixed height, variable width), so map
    // through the SVG's own screen transform rather than the element's bounding rect.
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = 0;
    const x = point.matrixTransform(ctm.inverse()).x;
    const index = Math.floor((x - padLeft) / slot);
    setActiveIndex(Math.max(0, Math.min(keys.length - 1, index)));
  }

  const activeRows =
    activeIndex === null
      ? []
      : series.map((item) => ({ ...item, value: item.values[activeIndex] ?? 0 }));

  return (
    <>
      <div
        className="relative overflow-x-auto"
        onPointerLeave={() => {
          if (!dragging) setActiveIndex(null);
        }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          className="h-56 min-w-[540px] w-full touch-none select-none"
          role="application"
          aria-label={ariaLabel}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(true);
            setActiveFromPointer(event.clientX);
          }}
          onPointerMove={(event) => setActiveFromPointer(event.clientX)}
          onPointerUp={(event) => {
            setDragging(false);
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={padLeft}
                x2={width - padRight}
                y1={yAt(tick)}
                y2={yAt(tick)}
                stroke="#e2e8f0"
              />
              <text x="0" y={yAt(tick) + 4} className="fill-slate-500 text-[10px]">
                {tick}
              </text>
            </g>
          ))}
          {keys.map((key, index) => {
            const origin = padLeft + index * slot;
            const inner = slot * 0.72;
            const x0 = origin + (slot - inner) / 2;
            return (
              <g key={key}>
                {activeIndex === index && (
                  <rect x={origin} y={padTop} width={slot} height={plotHeight} fill="#f8fafc" />
                )}
                {stacked
                  ? stackRects(series, index, x0, inner, max, plotHeight, padTop)
                  : series.map((item, seriesIndex) => {
                      const gap = series.length > 1 ? 2 : 0;
                      const barW = (inner - gap * (series.length - 1)) / series.length;
                      const value = item.values[index] ?? 0;
                      const h = (value / max) * plotHeight;
                      return (
                        <rect
                          key={item.label}
                          x={x0 + seriesIndex * (barW + gap)}
                          y={padTop + plotHeight - h}
                          width={Math.max(barW, 1)}
                          height={Math.max(h, 0)}
                          fill={item.color}
                        />
                      );
                    })}
                {showTick(index) && (
                  <text
                    x={origin + slot / 2}
                    y={height - 8}
                    textAnchor="middle"
                    className="fill-slate-500 text-[10px]"
                  >
                    {shortDay(key)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {activeIndex !== null && (
          <div
            className="pointer-events-none absolute top-3 z-10 w-56 rounded-lg border border-line bg-white/95 p-2.5 text-xs shadow-lg"
            style={{ left: `${Math.min(68, Math.max(2, ((padLeft + activeIndex * slot) / width) * 100))}%` }}
          >
            <p className="mb-1.5 font-semibold text-ink">{tooltipTitle(keys[activeIndex] ?? "", unit, zone)}</p>
            <div className="space-y-1">
              {activeRows.map((row) => (
                <div key={row.label} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: row.color }} />
                  <span className="min-w-0 flex-1 truncate text-muted">{row.label}</span>
                  <span className="font-mono font-medium text-ink">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((item) => (
          <li key={item.label} className="flex items-center gap-1.5 text-xs text-muted">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
          </li>
        ))}
      </ul>
    </>
  );
}

function valuesFor(buckets: SignupBucketCounts[], key: keyof SignupBucketCounts): number[] {
  return buckets.map((bucket) => bucket[key]);
}

function GuardCard({ guard, zone }: { guard: AdminSignupStats["guard"]; zone: string }) {
  const tripped = guard.status === "tripped";
  const days = quietDays(guard.tripsInWindow);
  return (
    <section className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <h2 className="font-semibold text-ink">Sign-up guard</h2>
      <p className="mt-2 text-sm text-ink">
        {tripped
          ? `Tripped since ${guard.trippedAt ? formatInstant(guard.trippedAt, zone, { locale: "en-US" }) : "an unknown time"} (${reasonLabel(guard.tripReason)})`
          : "Armed"}
      </p>
      <dl className="mt-3 space-y-1 text-sm text-muted">
        <div>Trips in the last 90 days: {guard.tripsInWindow}</div>
        <div>Current mode: {guard.signupMode}</div>
        {tripped && guard.reopenEligibleAt && (
          <div>
            Reopens automatically after {days ?? "the configured"} quiet days - earliest{" "}
            {formatInstant(guard.reopenEligibleAt, zone, { locale: "en-US" })}
          </div>
        )}
        {tripped && !guard.reopenEligibleAt && (
          <div>
            Manual reopen required:{" "}
            <code className="text-xs text-ink">.\scripts\set-signup-mode.ps1 -Mode open</code>
          </div>
        )}
        {guard.lastAttemptAt && (
          <div>
            Last sign-up attempt: {formatInstant(guard.lastAttemptAt, zone, { format: "dateTime", locale: "en-US" })}
          </div>
        )}
        {guard.lastReopenedAt && (
          <div>Last automatic reopen: {formatInstant(guard.lastReopenedAt, zone, { locale: "en-US" })}</div>
        )}
      </dl>
    </section>
  );
}

export function AdminSignupsPage() {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.groups.includes("admin"));
  const [zone] = useState(() => resolveDisplayZone());
  const [days, setDays] = useState<Days>(30);
  const [grain, setGrain] = useState<Grain>("day");
  const [data, setData] = useState<AdminSignupStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchAdminSignupStats(days)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load sign-ups");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, isAdmin]);

  const unit: Grain = days === 7 && grain === "hour" ? "hour" : "day";
  const keys = useMemo(
    () => zonedBucketRange(new Date(), unit === "hour" ? days * 24 : days, zone, unit),
    [days, unit, zone]
  );
  const buckets = useMemo(
    () => (data ? bucketSignupEvents(data.events, zone, unit, keys) : []),
    [data, zone, unit, keys]
  );
  const summary = useMemo(() => (data ? summarizeSignupEvents(data.events) : null), [data]);
  const recent = useMemo(() => (data ? [...data.events].reverse().slice(0, 50) : []), [data]);

  if (!isAdmin) return <Navigate to="/" replace />;

  const attemptSeries: ChartSeries[] = ATTEMPT_SERIES.map((item) => ({
    label: item.label,
    color: item.color,
    values: valuesFor(buckets, item.key),
  }));
  const confirmSeries: ChartSeries[] = CONFIRM_SERIES.map((item) => ({
    label: item.label,
    color: item.color,
    values: valuesFor(buckets, item.key),
  }));

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:py-10">
      <PageHeader
        title="Admin · Sign-ups"
        subtitle="Account totals are live from Cognito. Charts bucket sign-up events in your browser's time zone. Confirmed means the emailed sign-up code was entered, not MFA."
      />
      <AdminNav />
      <ErrorBanner message={error} />
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-line bg-surface p-1">
          {([7, 30, 90] as Days[]).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setDays(value);
                if (value !== 7) setGrain("day");
              }}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${days === value ? "bg-brand-600 text-white" : "text-muted hover:text-ink"}`}
            >
              {value}D
            </button>
          ))}
        </div>
        {days === 7 && (
          <div className="inline-flex rounded-lg border border-line bg-surface p-1">
            {(["day", "hour"] as Grain[]).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setGrain(value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${grain === value ? "bg-brand-600 text-white" : "text-muted hover:text-ink"}`}
              >
                {value === "day" ? "Day" : "Hour"}
              </button>
            ))}
          </div>
        )}
      </div>
      {loading ? (
        <p className="py-12 text-sm text-muted">Loading sign-ups…</p>
      ) : (
        data &&
        summary && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(
                [
                  ["Total accounts", String(data.accounts.total)],
                  ["Confirmed", String(data.accounts.confirmed)],
                  ["Unconfirmed", String(data.accounts.unconfirmed)],
                  [`Confirmation rate (${days}D)`, rateLabel(summary.confirmationRate)],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="rounded-xl border border-line bg-surface p-4 shadow-sm">
                  <p className="text-xs text-muted-sm">{label}</p>
                  <p className="mt-1 text-xl font-semibold text-ink">{value}</p>
                </div>
              ))}
            </div>
            {data.waitlist && (
              <Link
                to="/admin/waitlist"
                className="mt-5 block rounded-2xl border border-line bg-surface p-5 no-underline shadow-sm hover:border-brand-300"
              >
                <h2 className="font-semibold text-ink">Access requests</h2>
                <dl className="mt-3 grid gap-3 sm:grid-cols-3">
                  <div>
                    <dt className="text-xs text-muted-sm">Pending</dt>
                    <dd className="mt-1 text-xl font-semibold text-ink">{data.waitlist.pending}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-sm">Approved</dt>
                    <dd className="mt-1 text-xl font-semibold text-ink">{data.waitlist.approved}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-sm">Denied</dt>
                    <dd className="mt-1 text-xl font-semibold text-ink">{data.waitlist.denied}</dd>
                  </div>
                </dl>
              </Link>
            )}
            <div className="mt-5">
              <GuardCard guard={data.guard} zone={zone} />
            </div>
            <section className="mt-5 rounded-2xl border border-line bg-surface p-5 shadow-sm">
              <h2 className="font-semibold text-ink">Sign-up attempts</h2>
              <p className="mb-4 text-xs text-muted-sm">
                Stacked counts per {unit}. Times use {zone}. Allowed includes allow-list and approved
                sign-ups. Hover or drag to inspect a bucket.
              </p>
              <SignupChart
                keys={keys}
                unit={unit}
                zone={zone}
                series={attemptSeries}
                stacked
                ariaLabel="Sign-up attempts by outcome. Move or drag across the chart to inspect a bucket."
              />
            </section>
            <section className="mt-5 rounded-2xl border border-line bg-surface p-5 shadow-sm">
              <h2 className="font-semibold text-ink">Sign-ups vs confirmations</h2>
              <p className="mb-4 text-xs text-muted-sm">
                Allowed attempts beside confirmations in {zone}. A wide gap is the bot signal: the
                address was accepted and a verification email went out, but the code was never entered.
              </p>
              <SignupChart
                keys={keys}
                unit={unit}
                zone={zone}
                series={confirmSeries}
                stacked={false}
                ariaLabel="Allowed sign-ups beside confirmations. Move or drag across the chart to inspect a bucket."
              />
            </section>
            <section className="mt-5 overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
              <div className="border-b border-line p-5">
                <h2 className="font-semibold text-ink">Recent activity</h2>
                <p className="text-xs text-muted-sm">
                  Last 50 events, newest first. Times use {zone}. Rows marked approx. were backfilled
                  from logs.
                </p>
              </div>
              {recent.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted">No sign-up events in this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs text-muted-sm">
                      <tr>
                        <th className="px-4 py-3">Time</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Outcome</th>
                        <th className="px-4 py-3">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((event, index) => (
                        <tr key={`${event.at}-${event.kind}-${event.outcome ?? ""}-${index}`} className="border-t border-line">
                          <td className="px-4 py-3 whitespace-nowrap text-muted">
                            {formatInstant(event.at, zone, { format: "dateTime", locale: "en-US" })}
                          </td>
                          <td className="px-4 py-3 text-ink">
                            {event.email}
                            {event.approx ? <span className="ml-2 text-xs text-muted-sm">approx.</span> : null}
                          </td>
                          <td className="px-4 py-3">{outcomeLabel(event)}</td>
                          <td className="px-4 py-3 capitalize text-muted">{event.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )
      )}
    </div>
  );
}
